import { useHasAccess } from "./useHasAccess";
import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useLayoutEffect, useRef, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { prepareWorkflowImport } from "../../shared/prepareWorkflowImport";
import { parseZip } from "../../shared/zip";
import { navigate, routes } from "../lib/urls";
import { workflowImportSizeError } from "../lib/workflowImportValidation";

export function useWorkflowZipImport(
  spaceId: string | null,
  {
    onError,
    replace = false,
  }: { onError?: (message: string) => void; replace?: boolean } = {}
) {
  const z = useZero();
  const hasWriteAccess = useHasAccess("space:write", spaceId ?? undefined);
  const canWrite = !!spaceId && hasWriteAccess;
  const accessRef = useRef({ canWrite: false, spaceId });
  useLayoutEffect(() => {
    accessRef.current = { canWrite, spaceId };
    return () => {
      accessRef.current = { canWrite: false, spaceId };
    };
  }, [canWrite, spaceId]);
  const [importError, setImportError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const importWorkflowZip = async (file: File) => {
    const reportError = onError ?? setImportError;
    if (!spaceId || !canWrite) {
      reportError(
        "You need write access to the destination space to import a workflow."
      );
      return false;
    }
    if (isImporting) return false;
    setImportError(null);
    setIsImporting(true);
    const doImport = async (): Promise<boolean> => {
      let raw: Record<string, string>;
      try {
        raw = await parseZip(file);
      } catch (err) {
        reportError(err instanceof Error ? err.message : "Invalid ZIP file");
        return false;
      }

      if (
        !accessRef.current.canWrite ||
        accessRef.current.spaceId !== spaceId
      ) {
        reportError(
          "The import was canceled because the destination or your write access changed. Please try again."
        );
        return false;
      }

      const prepared = prepareWorkflowImport(raw, file.name);
      if (!prepared.ok) {
        reportError(prepared.error);
        return false;
      }

      const workflowId = nanoid(12);
      const branchId = nanoid(12);
      const commitId = nanoid(12);
      const chatId = nanoid(12);
      const systemEntryId = nanoid(12);

      const args = {
        id: workflowId,
        spaceId,
        branchId,
        commitId,
        chatId,
        systemEntryId,
        name: prepared.derivedName,
        sourceFileName: file.name,
        files: prepared.files,
        apps: prepared.apps,
      };
      const sizeError = workflowImportSizeError(args);
      if (sizeError) {
        reportError(sizeError);
        return false;
      }
      const result = z.mutate(sharedMutators.importWorkflow(args));
      const clientResult = (await result.client) as {
        type: string;
        error?: unknown;
      };
      if (clientResult.type === "error") {
        const e = clientResult.error;
        reportError(
          e instanceof Error ? e.message : "Failed to import workflow"
        );
        return false;
      }
      navigate(
        routes.space.workflow,
        { spaceId, workflowId, branchId },
        replace ? { replace, state: null } : undefined
      );
      return true;
    };
    return doImport().finally(() => setIsImporting(false));
  };

  return { importError, isImporting, importWorkflowZip, canWrite };
}
